import { Button, Modal, useToast } from '@orbind/ui'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { categoriaDoKind, fraseDaNotificacao, metaDaCategoria } from '../features/notification/categorias.ts'
import { GROUP_LABEL, exactLabel, groupOf, relativeLabel, type GroupId } from '../features/notification/datas.ts'
import {
  useMarcarLida,
  useMarcarNaoLida,
  useMarcarTodasLidas,
  useNotificacoes,
  type Notificacao,
} from '../features/notification/useNotificacoes.ts'
import '../pages/notifications.css'
import './painel-de-notificacoes.css'

/**
 * Painel de notificações do sino (SPEC-20261104-1219, pedido do Cristian:
 * "como a lista de status de membros, abrindo ao clicar e fechando ao clicar
 * fora; ao clicar numa notificação abra uma modal sobre o que estiver aberto;
 * não precisa ter workspace em cima").
 *
 * Mora no shell, ao lado do painel de Membros, e usa os mesmos dados e a mesma
 * anatomia de linha da Central (`pages/NotificationsPage`). Clicar numa linha
 * NÃO marca como lida (SPEC-20261104-1209): só o botão da modal.
 */
export function PainelDeNotificacoes({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const navigate = useNavigate()
  const toast = useToast()
  const consulta = useNotificacoes(aberto)
  const marcarLida = useMarcarLida()
  const marcarNaoLida = useMarcarNaoLida()
  const marcarTodas = useMarcarTodasLidas()
  const [selecionadaId, setSelecionadaId] = useState<string | null>(null)
  const [soNaoLidas, setSoNaoLidas] = useState(false)

  const itens = consulta.data?.itens ?? []
  const naoLidas = consulta.data?.naoLidas ?? 0
  const selecionada = itens.find((n) => n.id === selecionadaId) ?? null
  const now = useMemo(() => Date.now(), [aberto])

  const grupos = useMemo(() => {
    const baldes: { id: GroupId; itens: Notificacao[] }[] = [
      { id: 'today', itens: [] },
      { id: 'week', itens: [] },
      { id: 'before', itens: [] },
    ]
    for (const n of itens) {
      if (soNaoLidas && n.lida) continue
      baldes.find((b) => b.id === groupOf(n.criadaEm, now))?.itens.push(n)
    }
    return baldes.filter((b) => b.itens.length > 0)
  }, [itens, now, soNaoLidas])

  const abrirOItem = (n: Notificacao) => {
    setSelecionadaId(null)
    onFechar()
    if (n.kind === 'calendar_invite') {
      if (!n.calendarEventId) {
        toast.push({ tone: 'warning', message: 'O evento deste convite não existe mais.' })
        return
      }
      navigate(`/calendar?${n.data ? `data=${n.data}&` : ''}evento=${n.calendarEventId}`)
      return
    }
    if (!n.workItemId) {
      toast.push({ tone: 'warning', message: 'O Item de Trabalho desta notificação não existe mais.' })
      return
    }
    navigate(`/work-items/${n.workItemId}`)
  }

  if (!aberto) return null

  return (
    <>
      <aside className="npanel" aria-label="Notificações">
        <header className="npanel__head">
          <span className="npanel__title">Notificações</span>
          <span className="npanel__count">{naoLidas > 0 ? `${naoLidas} não lidas` : 'tudo lido'}</span>
          <span className="npanel__grow" />
          <button
            type="button"
            className="npanel__btn"
            aria-pressed={soNaoLidas}
            onClick={() => setSoNaoLidas((v) => !v)}
          >
            {soNaoLidas ? 'Todas' : 'Só não lidas'}
          </button>
          <button
            type="button"
            className="npanel__btn"
            disabled={naoLidas === 0 || marcarTodas.isPending}
            onClick={() => marcarTodas.mutate()}
          >
            Marcar todas como lidas
          </button>
        </header>
        <div className="npanel__body">
          {consulta.isPending ? (
            <div className="npanel__empty">Carregando…</div>
          ) : grupos.length === 0 ? (
            <div className="npanel__empty">
              {soNaoLidas ? 'Nenhuma notificação por ler.' : 'Nenhuma notificação por aqui.'}
            </div>
          ) : (
            grupos.map((g) => (
              <section key={g.id} className="nf-group" aria-label={GROUP_LABEL[g.id]}>
                <header className="nf-group__head">
                  <span className="nf-group__label">{GROUP_LABEL[g.id]}</span>
                  <span className="nf-group__count">{g.itens.length}</span>
                </header>
                {g.itens.map((n) => {
                  const categoria = categoriaDoKind(n.kind)
                  const meta = metaDaCategoria(categoria)
                  return (
                    <button
                      key={n.id}
                      type="button"
                      className={`nf-row${n.lida ? '' : ' nf-row--unread'}`}
                      onClick={() => setSelecionadaId(n.id)}
                    >
                      <span className={`nf-icon nf-icon--${meta.tone}`} aria-hidden>
                        <i className={meta.icon} />
                      </span>
                      <span className="nf-row__body">
                        <span className="nf-row__text">{fraseDaNotificacao(n)}</span>
                        <span className="nf-row__meta">
                          <span className="nf-row__cat">{categoria}</span>
                          <span className="nf-row__ctx">{n.code || 'Item de Trabalho'}</span>
                          <span className="nf-row__time">· {relativeLabel(n.criadaEm, now)}</span>
                        </span>
                      </span>
                      {n.lida ? null : <span className="nf-row__dot" aria-hidden />}
                    </button>
                  )
                })}
              </section>
            ))
          )}
        </div>
      </aside>

      {/* detalhe em modal, por cima do que estiver aberto */}
      <Modal open={selecionada !== null} onClose={() => setSelecionadaId(null)} title="Notificação" width={520}>
        {selecionada ? (
          <div>
            <div className="nmodal__head">
              <span className={`nf-icon nf-icon--${metaDaCategoria(categoriaDoKind(selecionada.kind)).tone}`} aria-hidden>
                <i className={metaDaCategoria(categoriaDoKind(selecionada.kind)).icon} />
              </span>
              <span className="nf-row__cat">{categoriaDoKind(selecionada.kind)}</span>
              <span className="npanel__grow" />
              <span className="nf-row__time" title={exactLabel(selecionada.criadaEm)}>
                {relativeLabel(selecionada.criadaEm, now)}
              </span>
            </div>
            <p className="nmodal__text">{fraseDaNotificacao(selecionada)}</p>
            <p className="nmodal__meta">
              {selecionada.autor} · {exactLabel(selecionada.criadaEm)}
              {selecionada.lida ? '' : ' · Não lida'}
            </p>
            <button type="button" className="nmodal__ctx" onClick={() => abrirOItem(selecionada)}>
              <span className="nmodal__code">
                {selecionada.kind === 'calendar_invite' ? (
                  <>
                    <i className="far fa-calendar-check" aria-hidden /> Evento
                  </>
                ) : (
                  <>
                    <i className="far fa-list-alt" aria-hidden /> {selecionada.code || 'Item de Trabalho'}
                  </>
                )}
              </span>
              <span className="nmodal__ctxtitle">{selecionada.titulo}</span>
              <i className="fas fa-arrow-right" aria-hidden />
            </button>
            <div className="nmodal__actions">
              <Button
                variant="ghost"
                icon={selecionada.lida ? 'far fa-envelope' : 'fas fa-check'}
                disabled={marcarLida.isPending || marcarNaoLida.isPending}
                onClick={() =>
                  selecionada.lida ? marcarNaoLida.mutate(selecionada.id) : marcarLida.mutate(selecionada.id)
                }
              >
                {selecionada.lida ? 'Marcar como não lida' : 'Marcar como lida'}
              </Button>
              <Button variant="primary" icon="fas fa-arrow-right" onClick={() => abrirOItem(selecionada)}>
                {selecionada.kind === 'calendar_invite' ? 'Abrir no calendário' : 'Abrir o item'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  )
}
